/**
 * The accounts-anchored cost model — the cost engine's new spine.
 *
 * A COST LINE is one row of the Management P&L (a monthly expense subcategory, or a
 * department's labour), carried at its real basis-season £ per month. Each line has
 * a COST BEHAVIOUR that says how it responds to the designed timetable:
 *   - fixed    : unchanged by the timetable (insurance, rent, finance, most
 *                permanent labour, own-metals maintenance) — carried straight through.
 *   - variable : scales with an activity DRIVER (running-days / working-turns /
 *                train-miles / passengers). The modelled £ = (basis £ per driver-unit)
 *                × the designed timetable's activity.
 *
 * Table 1 (source) = the basis months. Table 2 (simulated) = the modelled months.
 * The per-line differential is Table 2 − Table 1. The month totals feed the P&L,
 * scoring and the exec PDF; the variable lines feed the engine's marginal cost.
 *
 * Server source: GET /api/research/studio-costs (api/src/functions/studioCosts.ts).
 * Defaults here are EDITABLE in the Studio and persisted in app.StudioDefaults.
 */
import type { FleetLoco } from './types'
import { applyRollingStock, applyEngineFuel } from './fleetRegister'

export type CostLineKind = 'expense' | 'labour' | 'override'
export type CostBehaviour = 'fixed' | 'variable'
/** What a variable line scales with. */
export type CostDriver = 'runningDays' | 'workings' | 'trainMiles' | 'passengers'
export type LabourClass = 'Direct' | 'Semi Direct' | 'Indirect'

export const DRIVER_LABEL: Record<CostDriver, string> = {
  runningDays: 'running days',
  workings: 'working turns',
  trainMiles: 'train-miles',
  passengers: 'passengers',
}

/** Singular driver unit, for the per-unit rate column ("£6.93 / train-mile"). */
export const DRIVER_UNIT: Record<CostDriver, string> = {
  runningDays: 'running day',
  workings: 'working turn',
  trainMiles: 'train-mile',
  passengers: 'passenger',
}

/**
 * The per-unit £ rate a variable line is priced at — basis-season £ ÷ basis-season
 * driver units. THE double-check (Chris, 2026-09-02): the same one rate prices both
 * the source season and the modelled season, so it must read identically wherever
 * it is shown. Null for fixed lines and when the basis driver is zero.
 */
export function lineRate(line: CostLine, basis: Activity[]): { rate: number; unit: string } | null {
  if (line.behaviour !== 'variable') return null
  if (line.engineContract) {
    // A named engine is priced by its contract, not by a basis-struck average.
    const c = line.engineContract
    if (c.costPerMile > 0) return { rate: c.costPerMile, unit: 'timetabled mile' }
    if (c.dayRate > 0) return { rate: c.dayRate, unit: 'operating day' }
    if (c.maintenancePerMile > 0) return { rate: c.maintenancePerMile, unit: 'timetabled mile' }
    return null
  }
  const seasonDriver = basis.reduce((t, a) => t + a[line.driver], 0)
  if (seasonDriver <= 0) return null
  const seasonCost = line.months.reduce((a, b) => a + b, 0)
  return { rate: seasonCost / seasonDriver, unit: DRIVER_UNIT[line.driver] }
}

/** The rate formatted the ONE way it appears everywhere (screen + PDF). */
export function lineRateLabel(line: CostLine, basis: Activity[]): string | null {
  const r = lineRate(line, basis)
  if (!r) return null
  const gbp = r.rate >= 100
    ? Math.round(r.rate).toLocaleString('en-GB')
    : r.rate.toFixed(2)
  const c = line.engineContract
  if (c && c.costPerMile > 0 && c.dayRate > 0) return `£${gbp} / ${r.unit} + £${Math.round(c.dayRate).toLocaleString('en-GB')} / day`
  return `£${gbp} / ${r.unit}`
}

export interface CostLine {
  /** Stable id — subcategory key, dept code, or an override id. */
  id: string
  kind: CostLineKind
  /** P&L report category for expenses; 'Labour' section for labour; 'Override' otherwise. */
  category: string
  label: string
  /** Labour only. */
  dept?: string
  cls?: LabourClass
  seasonal?: boolean
  /** Basis-season actual £ by month (index 0 = March .. 11 = February), spend-positive. */
  months: number[]
  behaviour: CostBehaviour
  /** Only meaningful when behaviour === 'variable'. */
  driver: CostDriver
  /** Unchecked lines are excluded from the model and the totals. */
  included: boolean
  /** FIXED lines only. 0 = fully carried through (default). 1 = flex the full
   *  activity-proportional reduction. The realised saving is flex × the season's
   *  turns reduction, so the slider is capped at that reduction (Chris, 2026-09-01:
   *  "slide it to my satisfaction up the 50% mark"). */
  flex?: number
  /** A named engine's line (fleetRegister.ts::applyRollingStock): the register id. */
  engineId?: string
  /** Its contract — present on the engine's VARIABLE line, which is re-priced on
   *  the designed season's miles above the contracted minimum and its days. */
  engineContract?: EngineContract
  /** The memo of the P&L hire line before the rolling-stock deductions (never in totals). */
  rsMemo?: boolean
  /** The P&L hire line after the deductions — "Variable after Fixed". */
  rsResidual?: boolean
  /** A line DEDUCTED from a P&L line: the id of the P&L line it comes off (the
   *  named engines and Fixed Rolling Stock under Hire of Rolling Stock; the
   *  planner's custom fixed lines under any line — Chris, 2026-09-11). */
  parentId?: string
  /** The memo of the P&L line `memoOf` as the accounts had it, before any deduction (never in totals). */
  memoOf?: string
  /** The P&L line `residualOf` after its deductions — what remains of it. */
  residualOf?: string
  /** A planner-added fixed line (CostConfig.fixedLines) — named, Starter £, flat or on the P&L curve. */
  customFixed?: boolean
}

/**
 * A planner-added FIXED line beneath a P&L line (Chris, 2026-09-11: "when I
 * click edit allow me to add under any of the sidebar cost lines a custom fixed
 * cost line"). Named; a season £ in its "Starter" cell; laid across the months
 * either FLAT (the same twelfth every month of the season) or on the P&L line's
 * own COST CURVE (pro rata to the original line's months). Deducted from the
 * parent line, so what remains of the parent is its variable after the fixed.
 */
export interface CustomFixedLine {
  id: string
  /** The P&L line it sits under and comes off. */
  parentId: string
  label: string
  mode: 'flat' | 'curve'
  /** The season £ — the "Starter" cell. */
  starter: number
}

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)

/** One month's activity, used to scale variable lines. */
export interface Activity {
  runningDays: number
  workings: number
  trainMiles: number
  passengers: number
  /** TIMETABLED miles per register engine this month (key = engine id) — the
   *  driver of a named hire line (Chris, 2026-09-11). Absent without a register. */
  engineMiles?: Record<string, number>
  /** Operating days each register engine worked this month (a day-rate contract's days). */
  engineDays?: Record<string, number>
}
export const ZERO_ACTIVITY: Activity = { runningDays: 0, workings: 0, trainMiles: 0, passengers: 0 }

/** A named engine's contract, carried on its variable hire line so the
 *  simulation can re-price it on the DESIGNED season's miles and days. */
export interface EngineContract { costPerMile: number; dayRate: number; minContractMiles: number; maintenancePerMile: number }

/** The planner's "Fixed Rolling Stock" spread (Chris, 2026-09-11): a season £ of
 *  the P&L hire line that is really FIXED (contracted minimums not yet named
 *  engine by engine), spread across the running months and deducted from the
 *  variable hire. */
export interface RollingStockConfig {
  spread?: number
  /** Per-month cells overriding the equal spread (operating-month index → £). */
  spreadMonths?: Record<number, number>
}

/** Per-line saved config (only the deltas from default are persisted). */
export interface CostLineOverride {
  behaviour?: CostBehaviour
  driver?: CostDriver
  included?: boolean
  flex?: number
  /** Manual per-month £ overrides (sparse: index -> value). */
  months?: Record<number, number>
}
export interface CostConfig {
  basis?: string
  overrides?: Record<string, CostLineOverride>
  /** User-added override/addition lines (kind === 'override'). */
  additions?: CostLine[]
  /** The "Fixed Rolling Stock" spread (Chris, 2026-09-11). */
  rollingStock?: RollingStockConfig
  /** The planner's custom fixed lines under P&L lines (Chris, 2026-09-11). */
  fixedLines?: CustomFixedLine[]
}

// ---------------------------------------------------------------------------
//  Default behaviour classification — by P&L subcategory / labour type.
//  Chris steers these once against real numbers; every line is re-taggable.
// ---------------------------------------------------------------------------
interface Behave { behaviour: CostBehaviour; driver: CostDriver }
const FIXED: Behave = { behaviour: 'fixed', driver: 'runningDays' }
const V = (driver: CostDriver): Behave => ({ behaviour: 'variable', driver })

/** Expense subcategory -> default behaviour. Unlisted => fixed (a cost we can't
 *  attribute to running should not spuriously move with the timetable). */
const EXPENSE_DEFAULTS: Record<string, Behave> = {
  'Fuel, Oils and Lubricants': V('trainMiles'),
  'Traction & Rolling Stock': V('trainMiles'),
  'Hire of Rolling Stock': V('workings'),
  'Operating Costs': V('runningDays'),
  // Water is consumed by the trains that run, not by the calendar
  // (Chris, 2026-09-02: "water is costed by turns").
  'Water': V('workings'),
  'Catering Cost Of Sales': V('passengers'),
  'Retail Cost Of Sales': V('passengers'),
  'Diner Purchases': V('passengers'),
  'Conmat - Catering': V('passengers'),
  'Conmat - Diner': V('passengers'),
  'Conmat - Charter': V('passengers'),
  'Conmat - Shops': V('passengers'),
  // Held fixed by default (calendar/overhead, not per-train):
  'Infrastructure': FIXED,
  'Marketing': FIXED,
  'Marketing Special Events': FIXED,
  'Insurances': FIXED,
  'Electricity': FIXED,
  'IT & Software': FIXED,
  'Professional Support Costs': FIXED,
  'Professional Fees': FIXED,
  'Bank & Finance Charges': FIXED,
  'Rent & Rates': FIXED,
  'Other Employee Costs': FIXED,
  'Motor & Travel Expenses': FIXED,
  'Support Costs': FIXED,
  'Postage, Printing and Stationary': FIXED,
  'Gas & Oil': FIXED,
  'Telephones': FIXED,
  'Promotional Expenses': FIXED,
}

export function defaultExpenseBehaviour(subcategory: string): Behave {
  return EXPENSE_DEFAULTS[subcategory] ?? FIXED
}

/** Labour default: seasonal staff scale with running days; permanent staff are a
 *  fixed establishment cost (you don't stand down permanent crew for one fewer
 *  train). Re-tag e.g. footplate to variable-by-workings if wanted. */
export function defaultLabourBehaviour(seasonal: boolean): Behave {
  return seasonal ? V('runningDays') : FIXED
}

// ---------------------------------------------------------------------------
//  Build the line model from the API response + saved config.
// ---------------------------------------------------------------------------
export interface StudioCostsResponse {
  season: string
  entity: string
  months: { period_no: number; short: string; name: string; cal_year: number }[]
  has_data: boolean
  last_refresh: string | null
  expenseLines: { category: string; subcategory: string; months: number[]; total: number }[]
  labourLines: { label: string; dept: string; cls: LabourClass; seasonal: boolean; share: number; months: number[]; total: number }[]
  labourClassMonths: Record<LabourClass, number[]>
  labourClassTotals: Record<LabourClass, number>
  mix_note?: string
}

function applyOverride(line: CostLine, ov?: CostLineOverride): CostLine {
  if (!ov) return line
  const months = line.months.slice()
  if (ov.months) for (const [k, v] of Object.entries(ov.months)) months[Number(k)] = v
  return {
    ...line,
    behaviour: ov.behaviour ?? line.behaviour,
    driver: ov.driver ?? line.driver,
    included: ov.included ?? line.included,
    flex: ov.flex ?? line.flex,
    months,
  }
}

export function buildCostLines(res: StudioCostsResponse, cfg?: CostConfig): CostLine[] {
  const ov = cfg?.overrides ?? {}
  const expense: CostLine[] = res.expenseLines.map((e) => {
    const b = defaultExpenseBehaviour(e.subcategory)
    const id = `exp:${e.category}:${e.subcategory}`
    return applyOverride({
      id, kind: 'expense', category: e.category, label: e.subcategory,
      months: e.months.slice(), behaviour: b.behaviour, driver: b.driver, included: true,
    }, ov[id])
  })
  const labour: CostLine[] = res.labourLines
    .filter((l) => l.total !== 0 || l.share > 0)
    .map((l) => {
      const b = defaultLabourBehaviour(l.seasonal)
      const id = `lab:${l.cls}:${l.dept}:${l.label}`
      return applyOverride({
        id, kind: 'labour', category: `Labour — ${l.cls}`, label: l.label,
        dept: l.dept, cls: l.cls, seasonal: l.seasonal,
        months: l.months.slice(), behaviour: b.behaviour, driver: b.driver, included: true,
      }, ov[id])
    })
  const additions: CostLine[] = (cfg?.additions ?? []).map((a) => applyOverride(a, ov[a.id]))
  return [...expense, ...labour, ...additions]
}

/**
 * The legacy rate-card category £/month, seeded into "Overrides & Additions" so
 * the current line items are kept & editable but UNCHECKED by default (they never
 * double-count the P&L). Months are keyed by operating period (0 = March).
 */
export function buildLegacySeed(monthDays: { key: string; days: number; perDay?: Record<string, number> }[]): CostLine[] {
  if (!monthDays.length) return []
  const mk = (key: 'loco' | 'carriages' | 'crew' | 'infra' | 'overhead', label: string): CostLine => {
    const months = Array(12).fill(0) as number[]
    for (const mo of monthDays) {
      const cal = Number(mo.key.slice(5, 7))
      if (!(cal >= 1 && cal <= 12)) continue
      months[(cal - 3 + 12) % 12] += (mo.perDay?.[key] ?? 0) * mo.days
    }
    return { id: `ovr:legacy:${key}`, kind: 'override', category: 'Override', label, months, behaviour: 'fixed', driver: 'runningDays', included: false }
  }
  return [
    mk('loco', 'Locomotives (rate card)'),
    mk('carriages', 'Carriages (rate card)'),
    mk('crew', 'Train crew (rate card)'),
    mk('infra', 'Infrastructure (rate card)'),
    mk('overhead', 'Fixed overheads (rate card)'),
  ]
}

// ---------------------------------------------------------------------------
//  The simulation — Table 2 from Table 1 + activity.
// ---------------------------------------------------------------------------
const driverOf = (a: Activity, d: CostDriver): number => a[d]

/**
 * The season activity-proportional reduction, on the reference driver
 * (working-turns by default): 1 − modelled/basis, clamped to [0,1]. This is the
 * CEILING on how far a fixed line's flex slider can pull its cost down — a fixed
 * line can never be reduced by more than the activity actually fell.
 */
export function proportionalReduction(basis: Activity[], model: Activity[], driver: CostDriver = 'workings'): number {
  const b = basis.reduce((t, a) => t + a[driver], 0)
  const m = model.reduce((t, a) => t + a[driver], 0)
  if (b <= 0) return 0
  return clamp01(1 - m / b)
}

/**
 * Modelled £ per month for one line.
 *  - fixed    : the basis month shape, optionally pulled down by the flex slider
 *               (flex × the capped season reduction, applied uniformly).
 *  - variable : re-priced at the basis RATE (season £ ÷ season basis-driver) times
 *               the designed timetable's activity that month — so a month the basis
 *               never ran can still be priced, and cutting workings cuts the cost
 *               proportionally.
 * `fixedFlexReduction` is the season proportionalReduction() and only affects fixed
 * lines that carry a flex > 0.
 */
export function simulateLine(line: CostLine, basis: Activity[], model: Activity[], fixedFlexReduction = 0): number[] {
  if (line.behaviour === 'fixed') {
    const f = clamp01(line.flex ?? 0) * clamp01(fixedFlexReduction)
    if (f <= 0) return line.months.slice()
    return line.months.map((v) => Math.round(v * (1 - f) * 100) / 100)
  }
  if (line.engineContract && line.engineId) {
    // A NAMED ENGINE: its contract priced on the designed season — £/mile on
    // EVERY mile the roster gives it (paid on the miles it runs, Chris
    // 2026-10-28; the contracted minimum is the roster's priority, not a
    // charge), plus its day rate on the days it works, plus any maintenance per
    // mile. An engine's own fuel line rides the same path (db/125). With no
    // engine activity in the model yet, the basis is held.
    const anyEngine = model.some((a) => a.engineMiles != null)
    if (!anyEngine) return line.months.slice()
    const c = line.engineContract
    const miles = model.map((a) => a.engineMiles?.[line.engineId!] ?? 0)
    const days = model.map((a) => a.engineDays?.[line.engineId!] ?? 0)
    return miles.map((m, i) => Math.round((c.costPerMile * m + c.dayRate * days[i] + c.maintenancePerMile * m) * 100) / 100)
  }
  const r = lineRate(line, basis)
  if (!r) return line.months.slice() // can't derive a rate — hold
  return model.map((a) => Math.round(r.rate * driverOf(a, line.driver) * 100) / 100)
}

export interface LineResult {
  line: CostLine
  basisMonths: number[]
  modelMonths: number[]
  basisTotal: number
  modelTotal: number
  delta: number
  /** The per-unit £ this line is priced at (variable lines only) — the SAME one
   *  rate prices basis and modelled, formatted identically on screen and PDF. */
  rateLabel: string | null
}
export interface SimResult {
  lines: LineResult[]
  basisTotal: number
  modelTotal: number
  /** Modelled £ per month across all included lines — feeds the P&L. */
  modelMonthTotals: number[]
  basisMonthTotals: number[]
  /** The season turns reduction (0..1) — the ceiling on every fixed line's flex. */
  flexCap: number
}

export function simulate(lines: CostLine[], basis: Activity[], model: Activity[]): SimResult {
  const flexCap = proportionalReduction(basis, model)
  const results: LineResult[] = []
  const basisMonthTotals = Array(12).fill(0) as number[]
  const modelMonthTotals = Array(12).fill(0) as number[]
  let basisTotal = 0, modelTotal = 0
  for (const line of lines) {
    const basisMonths = line.months.slice()
    const modelMonths = simulateLine(line, basis, model, flexCap)
    const bT = basisMonths.reduce((a, b) => a + b, 0)
    const mT = modelMonths.reduce((a, b) => a + b, 0)
    results.push({ line, basisMonths, modelMonths, basisTotal: bT, modelTotal: mT, delta: mT - bT, rateLabel: lineRateLabel(line, basis) })
    if (line.included) {
      for (let i = 0; i < 12; i++) { basisMonthTotals[i] += basisMonths[i]; modelMonthTotals[i] += modelMonths[i] }
      basisTotal += bT; modelTotal += mT
    }
  }
  return { lines: results, basisTotal, modelTotal, modelMonthTotals, basisMonthTotals, flexCap }
}

/**
 * The one place the whole cost picture is assembled — used by BOTH the on-screen
 * tables and the exec PDF so they can never disagree. Merges the legacy rate-card
 * seed into Overrides & Additions, builds the lines from saved config, and
 * simulates against the designed activity (falling back to the basis activity —
 * i.e. Table 2 = Table 1 — until a season is built).
 */
/** What the register adds to the cost model: the ticked engines, the months
 *  with running days (for the spread), and each engine's basis-season miles and
 *  days by month (from today's timetable, or the COO's figure). */
export interface RollingStockInput {
  locos: FleetLoco[] | undefined
  runningDays: number[]
  basisEngines: { miles: Record<string, number[]>; days: Record<string, number[]> } | undefined
}

export function buildSim(
  data: StudioCostsResponse,
  config: CostConfig,
  legacySeed: CostLine[],
  basisActivity: Activity[],
  modelActivity: Activity[],
  rolling?: RollingStockInput,
): { sim: SimResult; lines: CostLine[]; hasModel: boolean; warnings: string[] } {
  const seeded: CostConfig = { ...config, additions: [...legacySeed, ...(config.additions ?? [])] }
  let lines = buildCostLines(data, seeded)
  let warnings: string[] = []
  if (rolling) {
    // The register's named hire lines and the "Fixed Rolling Stock" spread, all
    // deducted from the P&L hire line (Chris, 2026-09-11).
    const rs = applyRollingStock(lines, config.rollingStock, rolling.locos, rolling.runningDays, rolling.basisEngines)
    lines = rs.lines
    warnings = rs.warnings
    // Each engine's own fuel & oils, off the P&L fuel line (db/125, Chris 2026-10-28).
    const fu = applyEngineFuel(lines, rolling.locos, rolling.runningDays, rolling.basisEngines)
    lines = fu.lines
    warnings = [...warnings, ...fu.warnings]
  }
  // The planner's custom fixed lines, each deducted from the P&L line it sits
  // under (after the register, so a line under Hire of Rolling Stock stacks
  // with the engines and the spread).
  const fx = applyFixedLines(lines, config.fixedLines, config.overrides)
  lines = fx.lines
  warnings = [...warnings, ...fx.warnings]
  const hasModel = modelActivity.reduce((t, a) => t + a.workings, 0) > 0
  const sim = simulate(lines, basisActivity, hasModel ? modelActivity : basisActivity)
  return { sim, lines, hasModel, warnings }
}

const OP_MONTHS = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']
const pence = (v: number) => Math.round(v * 100) / 100

/**
 * Lay a season £ across the twelve months: FLAT = the same twelfth every month
 * (the rounding pennies on February); CURVE = pro rata to the given P&L line's
 * months (a line with nothing in it falls back to flat). Never negative.
 */
export function layFixedLine(starter: number, mode: 'flat' | 'curve', curve: number[]): number[] {
  const total = Math.max(0, Number.isFinite(starter) ? starter : 0)
  const out = Array(12).fill(0) as number[]
  if (total <= 0) return out
  const shape = curve.reduce((t, v) => t + Math.max(0, v), 0)
  if (mode === 'curve' && shape > 0) {
    for (let i = 0; i < 12; i++) out[i] = pence(total * Math.max(0, curve[i] ?? 0) / shape)
  } else {
    const share = pence(total / 12)
    for (let i = 0; i < 12; i++) out[i] = share
  }
  // The season adds up to the Starter to the penny.
  const sum = out.reduce((t, v) => t + v, 0)
  const last = mode === 'curve' && shape > 0 ? out.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0).pop() ?? 11 : 11
  out[last] = pence(out[last] + (total - sum))
  return out
}

/**
 * THE PLANNER'S CUSTOM FIXED LINES (Chris, 2026-09-11). Each sits under the P&L
 * line it names and comes off it: the line keeps a MEMO of the accounts figure
 * (never in the totals), the fixed lines follow, and the line itself becomes
 * what remains after them, floored at £0 per month and WARNED where the fixed
 * lines exceed it. A line already carrying deductions (Hire of Rolling Stock
 * after the register) stacks: its memo and label stand, the new lines join its
 * children, and "cost-curve" follows the ORIGINAL P&L line's months.
 */
export function applyFixedLines(lines: CostLine[], fixedLines: CustomFixedLine[] | undefined, overrides?: Record<string, CostLineOverride>): { lines: CostLine[]; warnings: string[] } {
  const warnings: string[] = []
  if (!fixedLines || fixedLines.length === 0) return { lines, warnings }
  let out = lines.slice()
  const parents = [...new Set(fixedLines.map((f) => f.parentId))]
  for (const parentId of parents) {
    const at = out.findIndex((l) => l.id === parentId)
    if (at < 0) continue // the P&L line is not in this basis — the planner's line waits for it
    const parent = out[at]
    const existingMemo = out.find((l) => l.memoOf === parentId)
    const original = existingMemo ?? parent
    // The P&L line's plain name for the warnings (the memo's label carries a suffix).
    const plainLabel = (existingMemo?.label ?? parent.label).replace(/ \(P&L, before the deductions below\)$/, '')
    const children: CostLine[] = fixedLines.filter((f) => f.parentId === parentId).map((f) => {
      const c: CostLine = {
        id: f.id, kind: parent.kind, category: parent.category, label: f.label.trim() || 'Fixed line',
        months: layFixedLine(f.starter, f.mode, original.months), behaviour: 'fixed', driver: 'runningDays',
        included: true, parentId, customFixed: true,
      }
      const ov = overrides?.[f.id]
      if (ov?.flex != null) c.flex = ov.flex
      return c
    })
    const sum = Array(12).fill(0) as number[]
    for (const c of children) for (let i = 0; i < 12; i++) sum[i] += c.months[i]
    const after = parent.months.map((v, i) => Math.max(0, pence(v - sum[i])))
    for (let i = 0; i < 12; i++) {
      if (sum[i] > parent.months[i] + 0.5) warnings.push(`${OP_MONTHS[i]}: the fixed lines under "${plainLabel}" (£${Math.round(sum[i]).toLocaleString('en-GB')}) exceed what the P&L line has left (£${Math.round(parent.months[i]).toLocaleString('en-GB')}) — it is held at £0 there`)
    }
    const residual: CostLine = {
      ...parent, months: after, residualOf: parentId,
      label: parent.residualOf || parent.rsResidual ? parent.label : `${parent.label} — after the fixed lines below`,
    }
    const memo: CostLine[] = existingMemo ? [] : [{ ...parent, id: `memo:${parentId}`, label: `${parent.label} (P&L, before the deductions below)`, included: false, memoOf: parentId }]
    out = [...out.slice(0, at), ...memo, ...children, residual, ...out.slice(at + 1)]
  }
  return { lines: out, warnings }
}

/**
 * The grey line under a P&L line that carries deductions (Chris, 2026-09-11:
 * "a small grey line which shows the + or − of each month against the original
 * P&L total so I can adjust the costs until they match"): the deducted lines
 * summed, month by month, against the accounts figure. + = the fixed lines
 * exceed what the P&L carried; − = they fall short (what remains is still on
 * the variable line); — = they match. Null when the line has no memo.
 */
export function pnlReconciliation(sim: SimResult, parentId: string): { diff: number[]; total: number; original: number[] } | null {
  const memo = sim.lines.find((r) => r.line.memoOf === parentId)
  if (!memo) return null
  const children = sim.lines.filter((r) => r.line.parentId === parentId && r.line.included)
  const diff = Array(12).fill(0) as number[]
  for (let i = 0; i < 12; i++) {
    const s = children.reduce((t, r) => t + (r.basisMonths[i] ?? 0), 0)
    diff[i] = pence(s - (memo.basisMonths[i] ?? 0))
  }
  return { diff, total: pence(diff.reduce((t, v) => t + v, 0)), original: memo.basisMonths.slice() }
}

/**
 * The cost model expressed as a PER-DAY cost — what lets the accounts costs
 * price every figure in the Studio.
 *
 * FIXED costs belong to their MONTH (Chris, 2026-09-04: "The line costs money
 * and runs all year round. It is merely the standard services that you are
 * modelling… A month, February, for example, where there are no standard
 * services running will still have fixed costs. Those are part of the plan. I
 * need to see them. If I want to pro-rata them I will use the sliders"). A
 * month's fixed cost is therefore the P&L's fixed lines FOR THAT MONTH — never
 * the season's fixed total smeared over the running days, which hid the
 * non-running months and loaded February's insurance onto an April train.
 * `fixedMonthMod` carries the flex sliders; `fixedMonthCur` does not.
 *
 * VARIABLE costs are incurred only by running: per running day, per working
 * turn, per train-mile, per passenger, at the basis season's own rate.
 *
 * Summed over all twelve months this reconciles EXACTLY to the Modelling-costs
 * Table 2 total (the costlines gate proves it).
 */
export interface DayCostModel {
  rate: Record<CostDriver, number>
  /** The month's FIXED cost (£), by operating month (March = 0 … February = 11). */
  fixedMonthCur: number[]
  fixedMonthMod: number[]
  /** The named engines' VARIABLE hire (£) by operating month, both sides — priced
   *  by contract on the month's miles and days, so it is shared over the month's
   *  running days rather than struck as a per-unit rate (Chris, 2026-09-11). */
  engineMonthCur: number[]
  engineMonthMod: number[]
  milesPerWorking: number
}
/** Operating-month index (March = 0 … February = 11) of a calendar month 1..12. */
export const opMonthIdx = (calMonth: number): number => (calMonth - 3 + 12) % 12
export function buildDayCostModel(lines: CostLine[], sim: SimResult, basis: Activity[], milesPerWorking = 24): DayCostModel {
  const rate = marginalRates(lines, basis)
  const fixedMonthCur = Array(12).fill(0) as number[]
  const fixedMonthMod = Array(12).fill(0) as number[]
  const engineMonthCur = Array(12).fill(0) as number[]
  const engineMonthMod = Array(12).fill(0) as number[]
  for (const r of sim.lines) {
    if (!r.line.included) continue
    if (r.line.behaviour === 'fixed') {
      for (let i = 0; i < 12; i++) {
        fixedMonthCur[i] += r.basisMonths[i] ?? 0
        fixedMonthMod[i] += r.modelMonths[i] ?? 0
      }
    } else if (r.line.engineContract) {
      for (let i = 0; i < 12; i++) {
        engineMonthCur[i] += r.basisMonths[i] ?? 0
        engineMonthMod[i] += r.modelMonths[i] ?? 0
      }
    }
  }
  return { rate, fixedMonthCur, fixedMonthMod, engineMonthCur, engineMonthMod, milesPerWorking }
}
/** This month's named-engine hire (£), a running day's share of which is the day's. */
export function pnlMonthEngine(m: DayCostModel, side: 'mod' | 'cur', calMonth: number): number {
  return (side === 'cur' ? m.engineMonthCur : m.engineMonthMod)[opMonthIdx(calMonth)] ?? 0
}
/** This month's FIXED cost — the line runs all year, so a non-running month carries it too. */
export function pnlMonthFixed(m: DayCostModel, side: 'mod' | 'cur', calMonth: number): number {
  return (side === 'cur' ? m.fixedMonthCur : m.fixedMonthMod)[opMonthIdx(calMonth)] ?? 0
}
/** One running day's share of its month's fixed cost. */
export function pnlDayFixedShare(m: DayCostModel, side: 'mod' | 'cur', calMonth: number, runningDaysInMonth: number): number {
  return pnlMonthFixed(m, side, calMonth) / Math.max(1, runningDaysInMonth)
}
/** The VARIABLE cost of one running day from its activity — per running day,
 *  per working turn, per train-mile, per passenger. `miles` is the day's REAL
 *  train-miles when the caller has them; absent, the legacy full-line proxy
 *  (services × milesPerWorking) stands in — old snapshots saved before miles
 *  were recorded. A day that runs no standard services has no variable cost. */
export function pnlDayVariable(m: DayCostModel, a: { services: number; carriedPerDay: number; miles?: number | null }): number {
  if (a.services <= 0) return 0
  const miles = a.miles ?? a.services * m.milesPerWorking
  return m.rate.runningDays
    + m.rate.workings * a.services
    + m.rate.trainMiles * miles
    + m.rate.passengers * a.carriedPerDay
}
/** The accounts-anchored cost of ONE running day: its month's fixed share + its variable cost. */
export function pnlDayCost(m: DayCostModel, a: { services: number; carriedPerDay: number; miles?: number | null }, side: 'mod' | 'cur', month: { calMonth: number; runningDays: number }): number {
  // A running day carries its share of the month's named-engine hire too — a
  // variable cost priced by contract on the month, shared over its running days.
  const engine = a.services > 0 ? pnlMonthEngine(m, side, month.calMonth) / Math.max(1, month.runningDays) : 0
  return pnlDayFixedShare(m, side, month.calMonth, month.runningDays) + pnlDayVariable(m, a) + engine
}

/**
 * ONE working's VARIABLE cost on the P&L: the per-working-turn lines, plus the
 * per-train-mile lines for its own miles, plus the per-passenger lines for the
 * people who board it. The per-running-day lines and the fixed lines are the
 * DAY's cost, never one train's (law 1). The Season Actuals workbook, the board
 * paper's worked day and the drill-down all price a service by this one rule —
 * never the rate card (Chris, 2026-09-04: "does that mean you are not using the
 * P&L costs to calculate the locomotive cost per service?").
 */
export function pnlServiceVariable(m: Pick<DayCostModel, 'rate'>, s: { miles: number; pax: number }): number {
  return m.rate.workings + m.rate.trainMiles * s.miles + m.rate.passengers * s.pax
}

export interface PnlDayLine { label: string; kind: 'fixed' | 'variable'; detail: string; amount: number }
/**
 * A running day's cost LINE BY LINE on the P&L — every included FIXED line's
 * share of ITS MONTH (÷ the month's running days; the proposal's flex applied on
 * the modelled side), every included VARIABLE line at its basis rate × the day's
 * activity. Sums to `pnlDayCost` for the same day (the costlines gate proves
 * it), so a board-paper breakdown can never disagree with the money it explains.
 * A day that runs no standard service lists its fixed lines only.
 */
export function pnlDayLines(
  sim: SimResult,
  basis: Activity[],
  side: 'mod' | 'cur',
  calMonth: number,
  runningDays: number,
  a: { services: number; carriedPerDay: number; miles?: number | null },
  monthName: string,
  milesPerWorking = 24,
): PnlDayLine[] {
  const out: PnlDayLine[] = []
  const op = opMonthIdx(calMonth)
  const days = Math.max(1, runningDays)
  const miles = a.miles ?? a.services * milesPerWorking
  const units: Record<CostDriver, number> = { runningDays: 1, workings: a.services, trainMiles: miles, passengers: a.carriedPerDay }
  const unitTxt: Record<CostDriver, string> = {
    runningDays: '1 running day',
    workings: `${Math.round(a.services)} working turn${Math.round(a.services) === 1 ? '' : 's'}`,
    trainMiles: `${Math.round(miles)} train-miles`,
    passengers: `${Math.round(a.carriedPerDay)} passengers`,
  }
  const gbp = (v: number) => `£${Math.round(v).toLocaleString('en-GB')}`
  for (const r of sim.lines) {
    if (!r.line.included) continue
    if (r.line.behaviour === 'fixed') {
      const monthly = (side === 'cur' ? r.basisMonths : r.modelMonths)[op] ?? 0
      const flexed = side === 'mod' && Math.abs(monthly - (r.basisMonths[op] ?? 0)) > 0.5
      out.push({
        label: r.line.label, kind: 'fixed',
        detail: `${monthName}'s ${gbp(monthly)} ÷ ${days} running day${days === 1 ? '' : 's'}${flexed ? ' (flexed)' : ''}`,
        amount: monthly / days,
      })
    } else if (r.line.engineContract) {
      // A named engine's hire or own fuel: the month's £ on its miles run (and
      // days worked) shared over the running days.
      if (a.services <= 0) continue
      const monthly = (side === 'cur' ? r.basisMonths : r.modelMonths)[op] ?? 0
      out.push({
        label: r.line.label, kind: 'variable',
        detail: `${monthName}'s ${gbp(monthly)} by contract (${lineRateLabel(r.line, basis) ?? 'contract'}) ÷ ${days} running day${days === 1 ? '' : 's'}`,
        amount: monthly / days,
      })
    } else {
      if (a.services <= 0) continue
      const rate = lineRate(r.line, basis)
      if (!rate) continue
      out.push({
        label: r.line.label, kind: 'variable',
        detail: `${unitTxt[r.line.driver]} × ${lineRateLabel(r.line, basis) ?? ''}`,
        amount: rate.rate * units[r.line.driver],
      })
    }
  }
  return out
}

/** Total included variable cost per driver-unit — the accounts-derived marginal
 *  the optimiser uses in place of the rate-card marginal. One rate per driver. */
export function marginalRates(lines: CostLine[], basis: Activity[]): Record<CostDriver, number> {
  const seasonDriver: Record<CostDriver, number> = {
    runningDays: basis.reduce((t, a) => t + a.runningDays, 0),
    workings: basis.reduce((t, a) => t + a.workings, 0),
    trainMiles: basis.reduce((t, a) => t + a.trainMiles, 0),
    passengers: basis.reduce((t, a) => t + a.passengers, 0),
  }
  const out: Record<CostDriver, number> = { runningDays: 0, workings: 0, trainMiles: 0, passengers: 0 }
  for (const l of lines) {
    if (!l.included || l.behaviour !== 'variable') continue
    // A named engine's hire is contract-shaped (miles above a minimum, day
    // rates) and is priced by month, not as a per-unit rate — never folded into
    // the per-driver marginal (Chris, 2026-09-11: the optimiser keeps the rate
    // card until he rules on engine-level marginal cost).
    if (l.engineContract) continue
    const d = seasonDriver[l.driver]
    if (d > 0) out[l.driver] += l.months.reduce((a, b) => a + b, 0) / d
  }
  return out
}
